"use client";

import type React from "react";
import { useState, useCallback, useEffect } from "react";
import { Box, Select, Textarea, Text, useToast } from "@chakra-ui/react";
import {
  agentService,
  type RetellLLM,
  type UpdateRetellLLMParams,
} from "../../voice-services/agentService";

interface WelcomeMessageSelectProps {
  initialLLM: RetellLLM;
  agentId: string;
  onLLMUpdate: (updatedLLM: RetellLLM) => void;
}

export const WelcomeMessageSelect: React.FC<WelcomeMessageSelectProps> = ({
  initialLLM,
  agentId,
  onLLMUpdate,
}) => {
  const toast = useToast();

  const [welcomeMessageType, setWelcomeMessageType] =
    useState<string>("user_initiates");
  const [welcomeMessage, setWelcomeMessage] = useState<string>("");

  useEffect(() => {
    if (!initialLLM) return;

    if (initialLLM.begin_message === "") {
      setWelcomeMessageType("user_initiates");
      setWelcomeMessage("");
    } else if (initialLLM.begin_message == null) {
      setWelcomeMessageType("dynamic");
      setWelcomeMessage("");
    } else {
      setWelcomeMessageType("defined");
      setWelcomeMessage(initialLLM.begin_message);
    }
  }, [initialLLM]);

  const handleWelcomeMessageChange = useCallback(
    async (newType: string, newMessage?: string) => {
      setWelcomeMessageType(newType);

      let newValue: string | null;
      if (newType === "user_initiates") {
        newValue = "";
      } else if (newType === "defined") {
        newValue = newMessage ?? "";
      } else {
        newValue = null;
      }

      setWelcomeMessage(newValue ?? "");

      try {
        const params: UpdateRetellLLMParams = {
          begin_message: newValue,
          // ✅ keep/ensure start_speaker (helps avoid validation issues)
          start_speaker: (initialLLM.start_speaker || "agent") as
            | "user"
            | "agent",
        };

        const updatedLLM = await agentService.updateRetellLLM(
          initialLLM.llm_id,
          params,
        );
        onLLMUpdate(updatedLLM);

        toast({
          title: "Welcome message updated",
          status: "success",
          duration: 2000,
        });
      } catch (error: any) {
        toast({
          title: "Error updating welcome message",
          description:
            error.response?.data?.message || "An unexpected error occurred",
          status: "error",
          duration: 3000,
        });
      }
    },
    [initialLLM, onLLMUpdate, toast],
  );

  return (
    <Box>
      <Text fontSize="sm" mb={2} opacity={0.7}>
        Welcome Message
      </Text>
      <Select
        size="sm"
        border="none"
        bg="gray.50"
        value={welcomeMessageType}
        onChange={(e) =>
          e.target.value !== "defined"
            ? handleWelcomeMessageChange(e.target.value)
            : handleWelcomeMessageChange(
                e.target.value,
                "Hello, how can I help you today?",
              )
        }
      >
        <option value="user_initiates">
          User Initiates: AI remains silent until users speak first.
        </option>
        <option value="dynamic">
          AI Initiates: AI begins with a dynamic begin message.
        </option>
        <option value="defined">
          AI Initiates: AI begins with your defined begin message.
        </option>
      </Select>

      {welcomeMessageType === "defined" && (
        <Textarea
          sx={{
            "&::-webkit-scrollbar": {
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "var(--chakra-colors-chakra-body-bg)",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "var(--chakra-colors-gray-400)",
              borderRadius: "2px",
            },
            scrollbarWidth: "thin",
            scrollbarColor:
              "var(--chakra-colors-gray-400) var(--chakra-colors-chakra-body-bg)",
          }}
          mt={2}
          rows={1}
          value={welcomeMessage}
          onChange={(e) => setWelcomeMessage(e.target.value)}
          onBlur={(e) => handleWelcomeMessageChange("defined", e.target.value)}
          placeholder="Add your defined begin message..."
          size="sm"
          border="none"
          bg="gray.50"
          borderColor="gray.200"
          _focus={{
            borderColor: "brand.500",
            boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
          }}
        />
      )}
    </Box>
  );
};
